import { type FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';

import { useAppSelector } from '@/redux/store';

import css from './styles.module.scss';

function formatRemaining(targetIso: string, nowMs: number): string {
  const diff = Math.max(0, new Date(targetIso).getTime() - nowMs);
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff / (1000 * 60 * 60)) % 24);
  const minutes = Math.floor((diff / (1000 * 60)) % 60);
  const seconds = Math.floor((diff / 1000) % 60);

  if (days > 0) return `${days}d : ${String(hours).padStart(2, '0')}h`;

  if (hours > 0) return `${hours}h : ${String(minutes).padStart(2, '0')}m`;

  return `${minutes}m : ${String(seconds).padStart(2, '0')}s`;
}

/**
 * Inline pill showing the time-to-end (when active) or time-to-start
 * (when pending). Hidden during 'inactive'. Uses serverNow + client
 * delta so the countdown can't be cheated by changing the device clock.
 */
const ScratchEventTimer: FC = () => {
  const { t } = useTranslation();
  const event = useAppSelector((s) => s.scratch.event);
  const [, setTick] = useState(0);

  useEffect(() => {
    const id = window.setInterval(() => setTick((n) => n + 1), 1000);

    return (): void => window.clearInterval(id);
  }, []);

  if (event.status === 'inactive' || !event.now) return null;

  const target = event.status === 'active' ? event.endAt : event.startAt;

  if (!target) return null;

  // Approximate "real now" using the offset captured at fetch time so
  // the countdown stays in sync with the server even if the device
  // clock is wrong. `event.now` is set on every API response by the
  // shared NowInterceptor.
  const clientDelta = Date.now() - event.now;
  const nowMs = Date.now() - clientDelta;
  const label = event.status === 'active' ? t('Time left') : t('Event starts');

  return (
    <div className={cn(css.pill, css[`status_${event.status}`])}>
      <span className={css.dot} />
      <span className={css.label}>{label}</span>
      <span className={css.value}>{formatRemaining(target, nowMs)}</span>
    </div>
  );
};

export { ScratchEventTimer };
